import { normalizeMarketZone } from "../../utils/marketSupport";

export const BALANCING_PRODUCTS = ["aFRR", "mFRR", "FCR"];

const DEFAULT_PRODUCT_ENTRY = {
  enabled: false,
  directions: { up: true, down: true },
  reservedUpMw: 0,
  reservedDownMw: 0,
  // Fixed-price contract valuation (optional; CONTRACTED_BASE_CASE only).
  // "" = not stated -> the engine values the contracted revenue at the
  // published historical price series and says so in the report.
  contractPriceValue: "",
  contractPriceUnit: "eur_per_mw_year",
};

export const BALANCING_CONTRACT_PRICE_UNIT_OPTIONS = [
  { value: "eur_per_mw_year", label: "EUR/MW per year" },
  { value: "eur_per_mw_hour", label: "EUR/MW per hour" },
];
const VALID_CONTRACT_PRICE_UNITS = new Set(
  BALANCING_CONTRACT_PRICE_UNIT_OPTIONS.map((option) => option.value)
);
function normalizeContractPriceUnit(value) {
  const unit = String(value || "").trim().toLowerCase();
  return VALID_CONTRACT_PRICE_UNITS.has(unit) ? unit : "eur_per_mw_year";
}
function normalizeContractPriceValue(value) {
  if (value === "" || value == null) return "";
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return "";
  return parsed;
}

// Activation-energy upside (disclosure-only, non-contracted, out of DSCR).
// Priced from ADMIE settlement data as balancing-market arbitrage on the
// reserved MW, capped by the battery's annual balancing throughput.
export const DEFAULT_BALANCING_ACTIVATION = {
  enabled: false,
  maxThroughputMwh: 0, // annual balancing throughput cap (MWh); 0 = uncapped
  cycleCostEurPerMwh: 0, // degradation cost per MWh throughput
  roundTripEfficiency: 0.88,
};

export const DEFAULT_BALANCING_V1_CONFIG = {
  // Multi-product configuration. aFRR stays the default active product so
  // existing projects (legacy single-product configs) behave identically.
  products: {
    aFRR: { ...DEFAULT_PRODUCT_ENTRY, enabled: true },
    mFRR: { ...DEFAULT_PRODUCT_ENTRY },
    FCR: { ...DEFAULT_PRODUCT_ENTRY },
  },
  priceTable: "balancing_reserve_prices",
  market: "GR",
  zoneCode: "GR",
  capacityPriceUnit: "EUR_PER_MW_PER_INTERVAL",
  revenueTreatment: "DISCLOSURE_ONLY",
  // A3(b): contract evidence for the contracted-base-case treatment.
  contract: {
    counterpartyStatus: "not_stated",
    tenorYears: "",
    priceBasis: "",
    creditSupport: "not_stated",
  },
  activation: DEFAULT_BALANCING_ACTIVATION,
};

export const BALANCING_CAPACITY_PRICE_UNIT_OPTIONS = [
  { value: "EUR_PER_MW_PER_INTERVAL", label: "EUR/MW per ISP (settlement period)" },
  { value: "EUR_PER_MW_PER_HOUR", label: "EUR/MW per hour" },
];
const VALID_CAPACITY_PRICE_UNITS = new Set(
  BALANCING_CAPACITY_PRICE_UNIT_OPTIONS.map((option) => option.value)
);
function normalizeCapacityPriceUnit(value) {
  return VALID_CAPACITY_PRICE_UNITS.has(value) ? value : "EUR_PER_MW_PER_INTERVAL";
}

export const BALANCING_V1_HELPER_TEXT =
  "Balancing models reserve-capacity availability revenue for the selected products (aFRR, mFRR, FCR). It does not include activation revenue, imbalance settlement, penalties, prequalification, or SoC reserve compliance. Note: balancing markets enforce minimum bid sizes (e.g. 1 MW in GR and DE); smaller units participate via an aggregator (fee not modelled).";

export const BALANCING_REVENUE_TREATMENT_OPTIONS = [
  {
    value: "DISCLOSURE_ONLY",
    label: "Disclosure only / upside case",
  },
  {
    value: "ALLOCATED_WITH_MERCHANT_SHARE",
    label: "Allocated with merchant share",
  },
  {
    value: "PROJECT_OWNER_100_PERCENT",
    label: "Project owner receives 100%",
  },
  {
    // A3(b) (2026-08-31): evidence-gated. Merchant arbitrage settlement with
    // regulatory framework "None" only; the engine downgrades anything else
    // to disclosure-only and prints why.
    value: "CONTRACTED_BASE_CASE",
    label: "Contracted — included in base case (evidence-gated)",
  },
];

export const BALANCING_REVENUE_TREATMENT_HELPER_TEXT =
  "Disclosure only is the conservative bankability default: balancing reserve revenue is shown as non-contracted upside and excluded from base-case NPV, IRR, DSCR and project revenue. Under tolling structures the allocation treatments only change the project/trader split. \"Contracted — included in base case\" admits the revenue to the base-case metrics: it requires merchant arbitrage settlement, regulatory framework \"None\", and complete contract evidence (binding term sheet or signed contract, tenor, price basis, declared credit support) — anything missing and the engine downgrades the run to disclosure-only with the reason printed in the report.";

export const BALANCING_CONTRACT_STATUS_OPTIONS = [
  { value: "not_stated", label: "Not stated" },
  { value: "indicative", label: "Indicative" },
  { value: "non_binding_term_sheet", label: "Non-binding term sheet" },
  { value: "binding_term_sheet", label: "Binding term sheet" },
  { value: "signed_contract", label: "Signed contract" },
];

export const BALANCING_CONTRACT_CREDIT_SUPPORT_OPTIONS = [
  { value: "not_stated", label: "Not stated" },
  { value: "none", label: "None (declared)" },
  { value: "parent_guarantee", label: "Parent guarantee" },
  { value: "bank_guarantee", label: "Bank guarantee / letter of credit" },
  { value: "cash_collateral", label: "Cash collateral" },
  { value: "other", label: "Other" },
];

const VALID_REVENUE_TREATMENTS = new Set(
  BALANCING_REVENUE_TREATMENT_OPTIONS.map((option) => option.value)
);

function parseFiniteNumber(value, fallback = 0) {
  if (value === "" || value == null) return fallback;
  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : fallback;
}

function normalizeDirectionSet(value, fallback = DEFAULT_PRODUCT_ENTRY.directions) {
  if (Array.isArray(value)) {
    const normalized = new Set(value.map((item) => String(item || "").toLowerCase()));
    return {
      up: normalized.has("up"),
      down: normalized.has("down"),
    };
  }
  if (value && typeof value === "object") {
    return {
      up: Boolean(value.up ?? value.Up ?? fallback.up),
      down: Boolean(value.down ?? value.Down ?? fallback.down),
    };
  }
  return { ...fallback };
}

function normalizeRevenueTreatment(value) {
  return VALID_REVENUE_TREATMENTS.has(value)
    ? value
    : DEFAULT_BALANCING_V1_CONFIG.revenueTreatment;
}

const VALID_CONTRACT_STATUSES = new Set(
  BALANCING_CONTRACT_STATUS_OPTIONS.map((option) => option.value)
);

function normalizeContract(source = {}) {
  const s = source || {};
  const status = String(s.counterpartyStatus ?? s.counterparty_status ?? "not_stated");
  const tenor = parseFiniteNumber(s.tenorYears ?? s.tenor_years, 0);
  return {
    counterpartyStatus: VALID_CONTRACT_STATUSES.has(status) ? status : "not_stated",
    tenorYears: tenor > 0 ? tenor : "",
    priceBasis: String(s.priceBasis ?? s.price_basis ?? "").trim(),
    creditSupport:
      String(s.creditSupport ?? s.credit_support ?? "not_stated").trim() || "not_stated",
  };
}

function normalizeActivation(source = {}) {
  const s = source || {};
  return {
    enabled: Boolean(s.enabled),
    maxThroughputMwh: Math.max(
      0,
      parseFiniteNumber(s.maxThroughputMwh ?? s.max_throughput_mwh, 0)
    ),
    cycleCostEurPerMwh: Math.max(
      0,
      parseFiniteNumber(s.cycleCostEurPerMwh ?? s.cycle_cost_eur_per_mwh, 0)
    ),
    roundTripEfficiency: parseFiniteNumber(
      s.roundTripEfficiency ?? s.round_trip_efficiency,
      DEFAULT_BALANCING_ACTIVATION.roundTripEfficiency
    ),
  };
}

function normalizeProductEntry(source = {}, defaults = DEFAULT_PRODUCT_ENTRY) {
  return {
    enabled: Boolean(source?.enabled ?? defaults.enabled),
    directions: normalizeDirectionSet(source?.directions, defaults.directions),
    reservedUpMw: parseFiniteNumber(
      source?.reservedUpMw ?? source?.reserved_up_mw,
      defaults.reservedUpMw
    ),
    reservedDownMw: parseFiniteNumber(
      source?.reservedDownMw ?? source?.reserved_down_mw,
      defaults.reservedDownMw
    ),
    contractPriceValue: normalizeContractPriceValue(
      source?.contractPriceValue ?? source?.contract_price_value
    ),
    contractPriceUnit: normalizeContractPriceUnit(
      source?.contractPriceUnit ?? source?.contract_price_unit
    ),
  };
}

function normalizeProducts(source = {}) {
  const rawProducts = source?.products;
  const products = {};
  if (rawProducts && typeof rawProducts === "object" && !Array.isArray(rawProducts)) {
    for (const name of BALANCING_PRODUCTS) {
      products[name] = normalizeProductEntry(rawProducts[name], {
        ...DEFAULT_PRODUCT_ENTRY,
        enabled: false,
      });
    }
    return products;
  }
  if (Array.isArray(rawProducts)) {
    for (const name of BALANCING_PRODUCTS) {
      const entry = rawProducts.find(
        (item) => String(item?.product || "").toLowerCase() === name.toLowerCase()
      );
      products[name] = entry
        ? { ...normalizeProductEntry(entry), enabled: true }
        : { ...DEFAULT_PRODUCT_ENTRY, enabled: false };
    }
    return products;
  }
  // Legacy single-product (aFRR) shape: map top-level keys onto aFRR.
  for (const name of BALANCING_PRODUCTS) {
    products[name] = { ...DEFAULT_PRODUCT_ENTRY, enabled: false };
  }
  products.aFRR = {
    enabled: true,
    directions: normalizeDirectionSet(source?.directions),
    reservedUpMw: parseFiniteNumber(
      source?.reservedUpMw ?? source?.reserved_up_mw,
      0
    ),
    reservedDownMw: parseFiniteNumber(
      source?.reservedDownMw ?? source?.reserved_down_mw,
      0
    ),
    contractPriceValue: "",
    contractPriceUnit: "eur_per_mw_year",
  };
  return products;
}

export function normalizeBalancingV1Config(source = {}, market = "GR") {
  // The balancing zone must follow the project's market (biddingZone, passed as
  // `market`), not a stale stored zoneCode. Otherwise an FR project whose config
  // still carries the old "GR" default queries balancing_reserve_prices with
  // zone_code=GR and finds no FR data. Project market wins; stored values are
  // only a fallback when no market is provided.
  // normalizeMarketZone: a German project's biddingZone is "DE_LU" (ENTSO-E zone
  // name) while balancing_reserve_prices.market and BALANCING_SUPPORTED_MARKETS
  // use the market code "DE". Storing the raw value made the backend validator
  // reject the run ("available only for markets [...]") before any query ran.
  const zone = normalizeMarketZone(
    market || source?.zoneCode || source?.zone_code || source?.market || "GR"
  );
  const products = normalizeProducts(source);
  return {
    products,
    priceTable: "balancing_reserve_prices",
    market: zone,
    zoneCode: zone,
    capacityPriceUnit: normalizeCapacityPriceUnit(
      source?.capacityPriceUnit ?? source?.capacity_price_unit
    ),
    revenueTreatment: normalizeRevenueTreatment(
      source?.revenueTreatment ?? source?.revenue_treatment
    ),
    contract: normalizeContract(source?.contract),
    activation: normalizeActivation(source?.activation),
  };
}

function enabledProducts(normalized) {
  return BALANCING_PRODUCTS.filter((name) => normalized.products[name]?.enabled);
}

function productTotals(normalized) {
  let up = 0;
  let down = 0;
  for (const name of enabledProducts(normalized)) {
    const entry = normalized.products[name];
    if (entry.directions.up) up += Math.max(0, parseFiniteNumber(entry.reservedUpMw, 0));
    if (entry.directions.down) down += Math.max(0, parseFiniteNumber(entry.reservedDownMw, 0));
  }
  return { up, down };
}

export function buildBalancingV1Payload(source = {}, market = "GR") {
  const normalized = normalizeBalancingV1Config(source, market);
  const products = enabledProducts(normalized).map((name) => {
    const entry = normalized.products[name];
    const directions = [];
    if (entry.directions.up) directions.push("Up");
    if (entry.directions.down) directions.push("Down");
    return {
      product: name,
      directions,
      reservedUpMw: entry.reservedUpMw,
      reservedDownMw: entry.reservedDownMw,
      contractPriceValue: entry.contractPriceValue === "" ? null : entry.contractPriceValue,
      contractPriceUnit: entry.contractPriceValue === "" ? null : entry.contractPriceUnit,
    };
  });
  return {
    enabled: true,
    products,
    priceTable: "balancing_reserve_prices",
    market: normalized.market,
    zoneCode: normalized.zoneCode,
    capacityPriceUnit: normalized.capacityPriceUnit,
    revenueTreatment: normalized.revenueTreatment,
    contract: {
      counterpartyStatus: normalized.contract.counterpartyStatus,
      tenorYears: normalized.contract.tenorYears || null,
      priceBasis: normalized.contract.priceBasis,
      creditSupport: normalized.contract.creditSupport,
    },
    activation: {
      enabled: Boolean(normalized.activation?.enabled),
      max_throughput_mwh: normalized.activation?.maxThroughputMwh || null,
      cycle_cost_eur_per_mwh: normalized.activation?.cycleCostEurPerMwh || 0,
      round_trip_efficiency: normalized.activation?.roundTripEfficiency || 0.88,
    },
  };
}

export function getBalancingV1Preview(source = {}, installedBessPowerMw = 0, market = "GR") {
  const normalized = normalizeBalancingV1Config(source, market);
  const installedPowerMw = Math.max(0, parseFiniteNumber(installedBessPowerMw, 0));
  const totals = productTotals(normalized);
  return {
    installedPowerMw,
    reservedUpMw: totals.up,
    reservedDownMw: totals.down,
    remainingDischargePowerMw: Math.max(0, installedPowerMw - totals.up),
    remainingChargePowerMw: Math.max(0, installedPowerMw - totals.down),
  };
}

export function validateBalancingV1Config({
  enabled,
  includedMarkets = {},
  balancingConfig = {},
  installedBessPowerMw = 0,
  market = "GR",
} = {}) {
  if (!enabled) return { errors: [], warnings: [] };

  const errors = [];
  const warnings = [];
  const normalized = normalizeBalancingV1Config(balancingConfig, market);
  const installedPowerMw = Math.max(0, parseFiniteNumber(installedBessPowerMw, 0));
  const active = enabledProducts(normalized);

  if (!includedMarkets.dam) {
    errors.push("DAM must be selected when Balancing is enabled.");
  }
  if (!active.length) {
    errors.push("Select at least one balancing product: aFRR, mFRR or FCR.");
  }

  for (const name of active) {
    const entry = normalized.products[name];
    const hasUp = Boolean(entry.directions.up);
    const hasDown = Boolean(entry.directions.down);
    const upMw = parseFiniteNumber(entry.reservedUpMw, 0);
    const downMw = parseFiniteNumber(entry.reservedDownMw, 0);
    if (!hasUp && !hasDown) {
      errors.push(`${name}: select at least one direction (Up or Down).`);
    }
    if (upMw < 0) {
      errors.push(`${name}: Reserved Up Capacity must be greater than or equal to 0 MW.`);
    }
    if (downMw < 0) {
      errors.push(`${name}: Reserved Down Capacity must be greater than or equal to 0 MW.`);
    }
  }

  const totals = productTotals(normalized);
  if (totals.up > installedPowerMw) {
    errors.push(
      `Total Reserved Up Capacity across products (${totals.up} MW) cannot exceed installed BESS power (${installedPowerMw} MW).`
    );
  }
  if (totals.down > installedPowerMw) {
    errors.push(
      `Total Reserved Down Capacity across products (${totals.down} MW) cannot exceed installed BESS power (${installedPowerMw} MW).`
    );
  }
  if (active.length && totals.up === 0 && totals.down === 0) {
    warnings.push(
      "Balancing is selected but all reserved capacities are zero, so balancing revenue will be zero."
    );
  }
  if (active.includes("FCR")) {
    warnings.push(
      "FCR price coverage in the ingested data can be sparse for Greece; hours without FCR data earn zero revenue."
    );
  }
  if (normalized.revenueTreatment === "CONTRACTED_BASE_CASE") {
    // All-or-nothing rule of the fixed-price valuation: the contract price
    // replaces the historical series only when EVERY active product states
    // one; a partial set silently falls back to historical, so warn here.
    const priced = active.filter(
      (name) => normalized.products[name]?.contractPriceValue !== ""
    );
    if (priced.length && priced.length < active.length) {
      const missing = active.filter(
        (name) => normalized.products[name]?.contractPriceValue === ""
      );
      warnings.push(
        `Contract price is stated for ${priced.join(", ")} but not for ${missing.join(
          ", "
        )}; the contracted revenue will be valued at the historical price series for ALL products until every product states a price.`
      );
    }
  }

  return { errors, warnings };
}

export function buildIncludedMarketsList(includedMarkets = {}) {
  const ordered = [
    ["dam", "DAM"],
    ["ida1", "IDA1"],
    ["ida2", "IDA2"],
    ["ida3", "IDA3"],
    ["balancing", "balancing"],
    ["capacity", "capacity"],
    ["go", "go"],
  ];
  return ordered
    .filter(([key]) => Boolean(includedMarkets[key]))
    .map(([, canonical]) => canonical);
}

export function includedMarketsToMap(includedMarkets = {}, fallbackStreams = {}) {
  const fromList = Array.isArray(includedMarkets)
    ? includedMarkets.reduce((acc, market) => {
        const key = String(market || "").toLowerCase();
        if (key === "dam") acc.dam = true;
        if (key === "ida1") acc.ida1 = true;
        if (key === "ida2") acc.ida2 = true;
        if (key === "ida3") acc.ida3 = true;
        if (key === "balancing") acc.balancing = true;
        if (key === "capacity") acc.capacity = true;
        if (key === "go") acc.go = true;
        return acc;
      }, {})
    : includedMarkets || {};
  return {
    dam: Boolean(fromList.dam ?? fallbackStreams.dam ?? true),
    ida1: Boolean(fromList.ida1 ?? fallbackStreams.ida1),
    ida2: Boolean(fromList.ida2 ?? fallbackStreams.ida2),
    ida3: Boolean(fromList.ida3 ?? fallbackStreams.ida3),
    balancing: Boolean(fromList.balancing ?? fallbackStreams.balancing),
    ancillary: false,
    capacity: Boolean(fromList.capacity ?? fallbackStreams.capacity),
    go: Boolean(fromList.go ?? fallbackStreams.go),
    other: false,
  };
}
